import { createCable, type Cable } from '@anycable/web';
import { apiClient } from '../api/axiosInstance';
import { ENDPOINTS } from '../api/endpoints';
import { parseHostname } from '../env/hostname';

/**
 * Derive the AnyCable WebSocket URL per-environment, mirroring `getApiBaseUrl()`:
 * the cable host is `wss://websocket.<env>.sera.tech/cable`, swapping the API's
 * `*.api.sera.tech` for `websocket.*`. NEVER hardcoded (CLAUDE.md Conventions) —
 * this replaces the harness's `wss://websocket.staging...` literal.
 *
 * The cable server has no per-tenant subdomain (a single `websocket.<env>` host
 * fans out per channel), so only the env segment matters. Precedence:
 *   1. DEV → explicit `VITE_LOCAL_CABLE_URL` override, else a localhost backend
 *      (`VITE_LOCAL_API_URL` pointing at localhost) → `ws://localhost:9080/cable`
 *      (anycable-go's local dev port), else `VITE_LOCAL_ENV` (empty → prod host).
 *   2. prod → hostname-derived env segment.
 */
export function getCableUrl(): string {
  if (import.meta.env.DEV) {
    const explicit = import.meta.env.VITE_LOCAL_CABLE_URL;
    if (explicit) return explicit;
    // Local backend: when the API points at localhost, AnyCable runs locally on
    // the anycable-go dev port over plain ws. Mirrors getApiBaseUrl()'s
    // VITE_LOCAL_API_URL localhost handling. Override the port via
    // VITE_LOCAL_CABLE_URL if your local cable server differs.
    const apiUrl = import.meta.env.VITE_LOCAL_API_URL;
    if (apiUrl && /\/\/(localhost|127\.0\.0\.1)/.test(apiUrl)) {
      return 'ws://localhost:9080/cable';
    }
    const env = import.meta.env.VITE_LOCAL_ENV;
    return env
      ? `wss://websocket.${env}.sera.tech/cable`
      : `wss://websocket.sera.tech/cable`;
  }
  const { envSegment, hasEnvSegment } = parseHostname(window.location.hostname);
  return hasEnvSegment
    ? `wss://websocket.${envSegment}.sera.tech/cable`
    : `wss://websocket.sera.tech/cable`;
}

interface CableAuthResponse {
  cable_token: string;
}

/**
 * D-06 token-refresh round-trip. The authenticated `apiClient` GETs
 * `/v1/users/cable_auth`; the server re-issues a customer-scoped `cable_token`.
 * The client never mints tokens — `setURL` only ever carries the server value.
 */
async function fetchCableToken(): Promise<string> {
  const { data } = await apiClient.get<CableAuthResponse>(
    ENDPOINTS.v1.users.cableAuth,
  );
  return data.cable_token;
}

export interface CreateAppCableOptions {
  /** The `cable_token` from the sign-in response — already in hand (D-06). */
  token: string;
  /**
   * Enable refresh-before-expiry. When truthy, the cable's `tokenRefresher` GETs
   * a fresh `cable_token` from `/v1/users/cable_auth` and re-points the transport.
   * Omitted (the magic-link/no-session path) → no refresher is wired and the
   * cable runs on the single initial token only.
   */
  refreshToken?: boolean;
  lazy?: boolean;
}

/**
 * TS port of the spike harness `createAppCable`. Builds an AnyCable Cable wired
 * with the `?token=` auth scheme and (when `refreshToken` is set) the D-06
 * cable_auth refresher.
 *
 * The initial connection uses `token` directly — no `/users/cable_auth` fetch on
 * construction (the sign-in response already carries it).
 */
export function createAppCable({
  token,
  refreshToken,
  lazy = true,
}: CreateAppCableOptions): Cable {
  const cableUrl = getCableUrl();
  // ME-03: percent-encode the credential so a token carrying `+`, `/`, `=` or
  // `&` cannot corrupt the auth query param.
  return createCable(`${cableUrl}?token=${encodeURIComponent(token)}`, {
    lazy,
    tokenRefresher: refreshToken
      ? async (transport) => {
          // WR-01: a rejecting tokenRefresher is fatal to AnyCable — it stops
          // the connection instead of retrying on the next monitor tick. A
          // transient /cable_auth failure (network blip, 5xx, brief session
          // expiry) must NOT be terminal, or one flaky refresh kills tracking
          // for the rest of the appointment (defeats D-06 resilience). Swallow
          // the error and keep the current token; the next refresh attempt
          // recovers.
          try {
            const fresh = await fetchCableToken();
            transport.setURL(`${cableUrl}?token=${encodeURIComponent(fresh)}`);
          } catch (err) {
            if (import.meta.env.DEV) {
              console.warn(
                '[createAppCable] cable_auth refresh failed; keeping current token',
                err,
              );
            }
            // Do not rethrow — keep the existing connection alive.
          }
        }
      : undefined,
  });
}
